import React, { useState, useEffect, useContext, useCallback, useRef, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../components/auth/AuthProvider';

// Custom hook for data fetching with better error handling
const useDataFetching = (fetchFn, initialState = []) => {
    const [data, setData] = useState(initialState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await fetchFn();

            if (result.success) {
                setData(result.data || []);
            } else {
                setError(result.message || 'Failed to load data');
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, [fetchFn]);

    const refetch = useCallback(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, loading, error, refetch };
};

// Reusable refresh button component
const RefreshButton = ({ onClick, isLoading, label = "Refresh" }) => (
    <button
        onClick={onClick}
        disabled={isLoading}
        className="ml-4 px-3 py-1 text-sm bg-accent hover:bg-opacity-90 text-primary font-medium rounded transition-all disabled:opacity-50"
        title={label}
    >
        {isLoading ? '...' : '↻'}
    </button>
);

// Memoize the entire ProfilePage component to prevent unnecessary re-renders
const ProfilePage = memo(() => {
    const { user, updateProfile, updatePassword, logout } = useContext(AuthContext);
    const navigate = useNavigate();

    // All hooks must be called before any conditional returns
    const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'password'

    // Profile info states
    const [username, setUsername] = useState(user?.username || '');
    const [firstName, setFirstName] = useState(user?.first_name || '');
    const [lastName, setLastName] = useState(user?.last_name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

    // Password states
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

    // Animation control ref to prevent re-animation on input change
    const profileAnimationRef = useRef(null);
    const passwordAnimationRef = useRef(null);

    // Download state tracking
    const [downloadingProducts, setDownloadingProducts] = useState(new Set());

    // Static fetch functions that won't change on re-renders
    const fetchOrdersFromAPI = useCallback(() => {
        console.log('Fetching orders from API...');
        console.log('User ID:', user?.id);

        if (!user?.id) {
            console.error('No user ID available for orders fetch');
            return Promise.resolve({ success: false, message: 'User not authenticated' });
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'https://api.interstellarmap.com';
        console.log('API URL:', apiUrl);

        return fetch(`${apiUrl}/orders`, {
            headers: {
                'X-User-ID': String(user.id), // Ensure it's a string
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                console.log('Orders response status:', response.status);
                if (!response.ok) {
                    if (response.status === 401) {
                        // Authentication failed, redirect to login
                        logout();
                        navigate('/login');
                        throw new Error('Authentication required. Please log in again.');
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.text();
            })
            .then(text => {
                console.log('Orders raw response:', text);
                if (!text) {
                    throw new Error('Empty response from server');
                }
                try {
                    const data = JSON.parse(text);
                    console.log('Orders received:', data.orders?.length || 0);
                    return { success: true, data: data.orders || [] };
                } catch (parseError) {
                    console.error('JSON parse error:', parseError);
                    console.error('Raw response:', text);
                    throw new Error('Invalid JSON response from server');
                }
            })
            .catch(error => {
                console.error('Error fetching orders:', error);
                return { success: false, message: error.message };
            });
    }, [user?.id, logout, navigate]);

    const fetchProductsFromAPI = useCallback(() => {
        console.log('Fetching products from API...');
        console.log('User ID:', user?.id);

        if (!user?.id) {
            console.error('No user ID available for products fetch');
            return Promise.resolve({ success: false, message: 'User not authenticated' });
        }

        const apiUrl = process.env.REACT_APP_API_URL || 'https://api.interstellarmap.com';
        console.log('API URL:', apiUrl);

        return fetch(`${apiUrl}/products`, {
            headers: {
                'X-User-ID': String(user.id), // Ensure it's a string
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                console.log('Products response status:', response.status);
                console.log('Products response headers:', Object.fromEntries(response.headers.entries()));

                if (!response.ok) {
                    if (response.status === 401) {
                        // Authentication failed, redirect to login
                        logout();
                        navigate('/login');
                        throw new Error('Authentication required. Please log in again.');
                    }
                    // Try to get error details from response
                    return response.text().then(text => {
                        console.error('Products API error:', text);
                        throw new Error(`HTTP ${response.status}: ${text || 'Server error'}`);
                    });
                }
                return response.text();
            })
            .then(text => {
                console.log('Products raw response:', text);
                if (!text) {
                    console.warn('Empty response from products API');
                    return { success: true, data: [] };
                }
                try {
                    const data = JSON.parse(text);
                    console.log('Products parsed response:', data);

                    // Handle different response formats
                    let products = [];
                    if (data.success && data.products) {
                        products = Array.isArray(data.products) ? data.products : [];
                    } else if (data.success && Array.isArray(data.data)) {
                        products = data.data;
                    } else if (Array.isArray(data)) {
                        products = data;
                    }

                    console.log('Products received:', products.length);

                    // Process map_data for each product
                    const processedProducts = products.map(product => {
                        if (product.map_data && typeof product.map_data === 'string') {
                            try {
                                product.map_data = JSON.parse(product.map_data);
                            } catch (e) {
                                console.error('Error parsing map_data for product:', product.id, e);
                                product.map_data = null;
                            }
                        }
                        return product;
                    });

                    return { success: true, data: processedProducts };
                } catch (parseError) {
                    console.error('JSON parse error:', parseError);
                    console.error('Raw response that failed to parse:', text);
                    throw new Error('Invalid JSON response from server');
                }
            })
            .catch(error => {
                console.error('Error fetching products:', error);
                // Return a more detailed error message
                return {
                    success: false,
                    message: `Failed to load star maps: ${error.message}. Please try refreshing the page.`
                };
            });
    }, [user?.id, logout, navigate]);

    // Use our custom hook for data fetching
    const { data: orders, loading: ordersLoading, error: ordersError, refetch: refetchOrders } =
        useDataFetching(fetchOrdersFromAPI, []);

    const { data: products, loading: productsLoading, error: productsError, refetch: refetchProducts } =
        useDataFetching(fetchProductsFromAPI, []);

    // Throttle function to prevent excessive API calls
    const throttle = (func, limit) => {
        let inThrottle;
        return function () {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    };

    // Throttled versions to prevent spam clicks
    const throttledOrdersRefresh = useCallback(throttle(refetchOrders, 5000), [refetchOrders]);
    const throttledProductsRefresh = useCallback(throttle(refetchProducts, 5000), [refetchProducts]);

    // Check if user is authenticated after all hooks are defined
    useEffect(() => {
        if (!user || !user.id) {
            console.log('User not authenticated, redirecting to login');
            navigate('/login');
            return;
        }
    }, [user, navigate]);

    // Update form fields when user changes
    useEffect(() => {
        if (user) {
            setUsername(user.username || '');
            setFirstName(user.first_name || '');
            setLastName(user.last_name || '');
            setEmail(user.email || '');
        }
    }, [user]);

    // Don't render anything if user is not properly authenticated
    if (!user || !user.id) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex justify-center items-center min-h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                </div>
            </div>
        );
    }

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setProfileMessage({ type: '', text: '' });

        if (!username || !email) {
            setProfileMessage({ type: 'error', text: 'Username and email are required' });
            return;
        }

        const result = await updateProfile(username, email, firstName, lastName);

        if (result.success) {
            setProfileMessage({ type: 'success', text: 'Profile updated successfully' });
        } else {
            setProfileMessage({ type: 'error', text: result.message || 'Profile update failed' });
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        setPasswordMessage({ type: '', text: '' });

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'Please fill in all fields' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        if (newPassword.length < 6) {
            setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters' });
            return;
        }

        const result = await updatePassword(currentPassword, newPassword);

        if (result.success) {
            setPasswordMessage({ type: 'success', text: 'Password updated successfully' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } else {
            setPasswordMessage({ type: 'error', text: result.message || 'Password update failed' });
        }
    };

    // Helper functions for formatting
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    // Helper function to format coordinates with proper spacing
    const formatCoordinates = (coordinates) => {
        if (!coordinates || typeof coordinates !== 'string') return coordinates;
        // Add space after comma if it doesn't exist
        return coordinates.replace(/,(?!\s)/g, ', ');
    };

    const getHdDownloadUrl = (product) => {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://api.interstellarmap.com';

        // Debug logging
        console.log('🔍 Generating download URL for product:', {
            id: product.id,
            product_name: product.product_name,
            pdf_url: product.pdf_url
        });

        // Extract the file ID from the PDF URL
        // PDF URL formats: 
        // - /downloads/starmap_{orderId}.pdf (old format)
        // - /downloads/starmap_package-{timestamp}-{id}-pdf.pdf (new format)
        if (product.pdf_url) {
            if (product.pdf_url.includes('starmap_package-')) {
                // New package format: the secure-download endpoint will handle finding the correct file
                // We'll use the product ID as the identifier
                const downloadUrl = `${apiUrl}/secure-download/pdf/${product.id}?user_id=${user.id}`;
                console.log('✅ Generated download URL for package format:', downloadUrl);
                return downloadUrl;
            } else if (product.pdf_url.includes('starmap_')) {
                // Old format: extract order ID
                const fileId = product.pdf_url.split('starmap_')[1].split('.')[0];
                const downloadUrl = `${apiUrl}/secure-download/pdf/${fileId}?user_id=${user.id}`;
                console.log('✅ Generated download URL for old format:', downloadUrl);
                return downloadUrl;
            } else if (product.pdf_url.startsWith('/downloads/')) {
                // Direct path format
                const filename = product.pdf_url.split('/').pop();
                const fileId = filename.split('.')[0].replace('starmap_', '');
                const downloadUrl = `${apiUrl}/secure-download/pdf/${fileId}?user_id=${user.id}`;
                console.log('✅ Generated download URL from direct path:', downloadUrl);
                return downloadUrl;
            }
        }

        // Fallback: use product ID if no PDF URL
        const fallbackUrl = `${apiUrl}/secure-download/pdf/${product.id}?user_id=${user.id}`;
        console.log('⚠️ Using fallback download URL:', fallbackUrl);
        return fallbackUrl;
    };

    // Handle download with better user feedback
    const handleDownload = async (product) => {
        const productId = product.id;

        // Add to downloading set
        setDownloadingProducts(prev => new Set(prev).add(productId));

        try {
            const downloadUrl = getHdDownloadUrl(product);
            console.log('🚀 Starting download for product:', product.product_name);

            // Create a temporary link to trigger download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `starmap_${product.product_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
            link.target = '_blank';

            // Add to DOM, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log('✅ Download initiated successfully');

            // Show success message
            setTimeout(() => {
                console.log('📁 Download should be starting in your browser');
            }, 1000);

        } catch (error) {
            console.error('❌ Download failed:', error);
            alert('Download failed. Please try again or contact support if the problem persists.');
        } finally {
            // Remove from downloading set after a short delay
            setTimeout(() => {
                setDownloadingProducts(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(productId);
                    return newSet;
                });
            }, 2000);
        }
    };

    // Tab content components
    const ProfileInformation = memo(() => {
        return (
            <div ref={profileAnimationRef} className="bg-black">
                {profileMessage.text && (
                    <div className={`mb-6 px-4 py-3 rounded ${profileMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {profileMessage.text}
                    </div>
                )}

                <form onSubmit={handleProfileUpdate} className="!bg-black">
                    <div className="mb-5">
                        <label className="block text-white text-sm font-bold mb-2" htmlFor="username">
                            Username
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-white bg-black leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="username"
                            type="text"
                            placeholder="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 mb-5 w-full">
                        <div className="bg-black w-full">
                            <label className="block text-white text-sm font-bold mb-2" htmlFor="firstName">
                                First Name
                            </label>
                            <input
                                className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-white bg-black leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                                id="firstName"
                                type="text"
                                placeholder="First Name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                        </div>
                        <div className="bg-black w-full">
                            <label className="block text-white text-sm font-bold mb-2" htmlFor="lastName">
                                Last Name
                            </label>
                            <input
                                className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-white bg-black leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                                id="lastName"
                                type="text"
                                placeholder="Last Name"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-white text-sm font-bold mb-2" htmlFor="email">
                            Email
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-white bg-black leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="email"
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="flex justify-end">
                        <button
                            className="bg-accent hover:bg-opacity-90 text-primary font-bold py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-all"
                            type="submit"
                        >
                            Update Profile
                        </button>
                    </div>
                </form>

                {/* Orders Section */}
                <div className="mt-12 pt-6 border-t border-gray-200 bg-black">
                    <div className="flex items-center mb-4">
                        <h3 className="text-xl font-bold text-white">Your Orders</h3>
                        <RefreshButton onClick={throttledOrdersRefresh} isLoading={ordersLoading} />
                    </div>

                    {ordersError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                            {ordersError}
                        </div>
                    )}

                    {ordersLoading ? (
                        <div className="flex justify-center my-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="text-center py-8 bg-black">
                            <p className="text-white mb-4">You haven't placed any orders yet.</p>
                            <Link to="/products" className="bg-accent hover:bg-opacity-90 text-primary font-bold py-2 px-4 rounded-lg">
                                Browse Products
                            </Link>
                        </div>
                    ) : (
                        <div className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-black">
                            <table className="min-w-full divide-y divide-gray-200 bg-black">
                                <thead className="bg-black">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Order #</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Date</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Total</th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-white uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-black divide-y divide-gray-200">
                                    {orders && orders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-900 bg-black">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">#{order.id}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-white">{formatDate(order.created_at)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-white">{formatPrice(order.total_amount)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Star Maps Section */}
                <div className="mt-12 pt-6 border-t border-gray-200 bg-black">
                    <div className="flex items-center mb-4">
                        <h3 className="text-xl font-bold text-white">Your Star Maps</h3>
                        <RefreshButton onClick={throttledProductsRefresh} isLoading={productsLoading} />
                    </div>

                    {productsError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                            {productsError}
                        </div>
                    )}

                    {productsLoading ? (
                        <div className="flex justify-center my-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-8 bg-black">
                            <p className="text-white mb-4">You haven't created any star maps yet.</p>
                            <Link to="/" className="bg-accent hover:bg-opacity-90 text-primary font-bold py-2 px-4 rounded-lg">
                                Create Your First Star Map
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                            {products.map((product) => (
                                <div key={product.id} className="rounded-lg overflow-hidden bg-black border border-gray-700 shadow-md transform transition-transform hover:scale-[1.02] hover:shadow-lg">
                                    <div className="p-4 bg-black">
                                        <h3 className="text-lg font-semibold mb-2 text-white">{product.product_name}</h3>
                                        <div className="text-sm text-white mb-2">Created on {formatDate(product.created_at)}</div>

                                        {product.map_data && (
                                            <div className="bg-black p-3 rounded text-sm mb-3 border border-gray-700">
                                                <p className="font-semibold mb-1 text-white">Map Details:</p>
                                                <div className="grid grid-cols-2 gap-2 bg-black">
                                                    {product.map_data.date && (
                                                        <>
                                                            <span className="text-white">Date:</span>
                                                            <span className="text-white">{product.map_data.date}</span>
                                                        </>
                                                    )}
                                                    {(product.map_data.locationName || product.map_data.location) && (
                                                        <>
                                                            <span className="text-white">Location:</span>
                                                            <span className="text-white">{product.map_data.locationName || formatCoordinates(product.map_data.location)}</span>
                                                        </>
                                                    )}
                                                    {product.map_data.caption && (
                                                        <>
                                                            <span className="text-white">Caption:</span>
                                                            <span className="text-white">{product.map_data.caption}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex flex-col gap-3 mt-4">
                                            {(product.pdf_url) ? (
                                                <button
                                                    onClick={() => handleDownload(product)}
                                                    disabled={downloadingProducts.has(product.id)}
                                                    className={`text-center py-2 px-4 rounded-lg font-medium text-sm transition-colors ${downloadingProducts.has(product.id)
                                                        ? 'bg-gray-500 cursor-not-allowed text-white'
                                                        : 'bg-green-500 hover:bg-green-600 text-white'
                                                        }`}
                                                >
                                                    {downloadingProducts.has(product.id) ? (
                                                        <div className="flex items-center justify-center">
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Downloading...
                                                        </div>
                                                    ) : (
                                                        'Download Star Map'
                                                    )}
                                                </button>
                                            ) : (
                                                <div className="text-amber-500 text-sm mb-2">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                    </svg>
                                                    Download will be available after payment is complete
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    });

    const PasswordSettings = memo(() => {
        useEffect(() => {
            if (passwordAnimationRef.current) {
                passwordAnimationRef.current.classList.add('animate-fade-in-up');
            }

            return () => {
                if (passwordAnimationRef.current) {
                    passwordAnimationRef.current.classList.remove('animate-fade-in-up');
                }
            };
        }, []);

        return (
            <div ref={passwordAnimationRef} className="bg-black">
                {passwordMessage.text && (
                    <div className={`mb-6 px-4 py-3 rounded ${passwordMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {passwordMessage.text}
                    </div>
                )}

                <form onSubmit={handlePasswordUpdate} className="bg-black">
                    <div className="mb-5">
                        <label className="block text-white text-sm font-bold mb-2" htmlFor="currentPassword">
                            Current Password
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-white bg-black leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="currentPassword"
                            type="password"
                            placeholder="Current Password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                    </div>

                    <div className="mb-5">
                        <label className="block text-white text-sm font-bold mb-2" htmlFor="newPassword">
                            New Password
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-white bg-black leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="newPassword"
                            type="password"
                            placeholder="New Password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-white text-sm font-bold mb-2" htmlFor="confirmPassword">
                            Confirm New Password
                        </label>
                        <input
                            className="appearance-none border border-gray-300 rounded-lg w-full py-3 px-4 text-white bg-black leading-tight focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
                            id="confirmPassword"
                            type="password"
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>

                    <div className="flex justify-end">
                        <button
                            className="bg-accent hover:bg-opacity-90 text-primary font-bold py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-all"
                            type="submit"
                        >
                            Update Password
                        </button>
                    </div>
                </form>
            </div>
        );
    });

    // Navigation link with icon
    const NavLink = memo(({ id, icon, label, onClick, isActive }) => (
        <div
            onClick={onClick}
            className={`flex items-center space-x-3 px-4 py-3 cursor-pointer rounded-lg mb-2 transition-all ${isActive
                ? 'bg-white text-black shadow-md'
                : 'hover:bg-gray-900 text-white'
                }`}
        >
            <div className={isActive ? 'text-black' : 'text-white'}>
                {icon}
            </div>
            <span className="font-medium">{label}</span>
        </div>
    ));

    // Get username and email for sidebar display only
    const displayUsername = user?.username || '';
    const displayEmail = user?.email || '';

    return (
        <div className="!bg-black pt-[50px] md:pt-[90px] pb-[50px]">
            <div className="container mx-auto px-4 py-16">
                <h1 className="text-3xl font-bold mb-6 text-white">Your Account</h1>

                <div className="flex flex-col md:flex-row gap-6">
                    {/* Sidebar */}
                    <div className="w-full md:w-72">
                        <div className="!bg-black p-6 rounded-lg shadow-sm">
                            <div className="flex flex-col items-center mb-6 pb-6 border-b border-gray-200">
                                <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center text-black text-2xl font-bold mb-3">
                                    {displayUsername.charAt(0).toUpperCase()}
                                </div>
                                <div className="text-center">
                                    <h2 className="text-lg font-bold text-white">{displayUsername}</h2>
                                    <p className="text-white text-sm">{displayEmail}</p>
                                </div>
                            </div>

                            <nav className="space-y-1">
                                <NavLink
                                    id="profile"
                                    isActive={activeTab === 'profile'}
                                    onClick={() => setActiveTab('profile')}
                                    icon={
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    }
                                    label="Profile Information"
                                />

                                <NavLink
                                    id="password"
                                    isActive={activeTab === 'password'}
                                    onClick={() => setActiveTab('password')}
                                    icon={
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    }
                                    label="Password"
                                />

                                <div
                                    onClick={logout}
                                    className="flex items-center space-x-3 px-4 py-3 cursor-pointer rounded-lg mt-6 text-red-600 hover:bg-red-50 transition-all"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                    <span className="font-medium">Log Out</span>
                                </div>
                            </nav>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="w-full md:flex-1">
                        <div className="bg-black p-8 rounded-lg shadow-sm">
                            {activeTab === 'profile' && (
                                <>
                                    <h2 className="text-2xl font-bold mb-6 text-white">Profile Information</h2>
                                    <ProfileInformation />
                                </>
                            )}

                            {activeTab === 'password' && (
                                <>
                                    <h2 className="text-2xl font-bold mb-6 !text-white">Password Settings</h2>
                                    <PasswordSettings />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default ProfilePage; 